import { FormEvent, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { useWorkspace } from "../api/hooks/useWorkspace";
import { useUpdateWorkspace } from "./useUpdateWorkspace";
import { slugErrorKind } from "./slugError";
import { useDeleteWorkspace } from "./useDeleteWorkspace";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { Spinner } from "../ui/Spinner";
import { useTranslation, apiErrorMessage } from "../i18n";
import { wsSettings, wsTokens, wsTrash } from "../lib/paths";

// ── Shared card chrome ───────────────────────────────────────────────────────

function Card({
  title,
  children,
  danger = false,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
  readonly danger?: boolean;
}) {
  return (
    <section
      className={
        danger
          ? "rounded-xl border border-red-900 bg-slate-900 p-6"
          : "rounded-xl border border-slate-800 bg-slate-900 p-6"
      }
    >
      <h2
        className={
          danger
            ? "mb-4 text-lg font-semibold text-red-400"
            : "mb-4 text-lg font-semibold text-slate-100"
        }
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

// ── General card (rename + change slug) ──────────────────────────────────────

function GeneralCard({ slug }: { readonly slug: string }) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const navigate = useNavigate();
  const ws = useWorkspace(slug);
  const update = useUpdateWorkspace(slug);

  const [name, setName] = useState("");
  const [wsSlug, setWsSlug] = useState("");
  const [seeded, setSeeded] = useState(false);

  // Seed fields once workspace data loads (useEffect to avoid setState-during-render)
  useEffect(() => {
    if (ws.data && !seeded) {
      setName(ws.data.name);
      setWsSlug(ws.data.slug);
      setSeeded(true);
    }
  }, [ws.data, seeded]);

  function handleSave(e: FormEvent) {
    e.preventDefault();
    update.mutate(
      { name: (name ?? "").trim(), slug: (wsSlug ?? "").trim() },
      {
        onSuccess: (updated) => {
          // If slug changed, navigate to the new URL
          if (updated.slug !== slug) {
            navigate(wsSettings(updated.slug), { replace: true });
          }
        },
      },
    );
  }

  const nameError =
    update.error?.code === "validation_failed"
      ? (update.error.details as { fieldErrors?: Record<string, string[]> } | null | undefined)
          ?.fieldErrors?.name?.[0]
      : undefined;
  const slugError =
    update.error?.code === "validation_failed"
      ? (update.error.details as { fieldErrors?: Record<string, string[]> } | null | undefined)
          ?.fieldErrors?.slug?.[0]
      : slugErrorKind(update.error) === "reserved"
        ? t("settings.slugReserved")
        : slugErrorKind(update.error) === "taken"
          ? t("settings.slugTaken")
          : undefined;
  // Derived from what actually rendered, rather than a second copy of the code
  // list that has to be kept in step with the one above.
  const genericError =
    update.error && update.error.code !== "validation_failed" && !slugError && !nameError
      ? apiErrorMessage(update.error, tErr)
      : undefined;

  const slugChanged = seeded && (wsSlug ?? "").trim() !== (ws.data?.slug ?? slug);

  if (ws.isLoading) {
    return (
      <Card title={t("settings.general")}>
        <div className="flex items-center justify-center py-8"><Spinner /></div>
      </Card>
    );
  }

  // Renaming/re-slugging is workspace:admin (owner-only) server-side; don't offer
  // an editable form to non-owners that always 403s on save (bugloop L1).
  const isOwner = ws.data?.role === "owner";

  return (
    <Card title={t("settings.general")}>
      {!isOwner ? (
        <Banner kind="info" className="mb-4">
          {t("settings.ownersOnly")}
        </Banner>
      ) : null}
      {genericError ? (
        <Banner kind="error" className="mb-4">{genericError}</Banner>
      ) : null}
      {update.isSuccess && !slugChanged ? (
        <Banner kind="success" className="mb-4">{t("settings.saved")}</Banner>
      ) : null}

      <form onSubmit={handleSave} className="space-y-4" noValidate>
        <Field label={t("settings.nameLabel")} error={nameError}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="text"
              autoComplete="off"
              required
              disabled={!isOwner}
              value={name}
              onChange={(e) => setName(e.target.value)}
              hasError={!!nameError}
              {...ariaProps}
            />
          )}
        </Field>

        <Field label={t("settings.slugLabel")} error={slugError}>
          {({ id, ...ariaProps }) => (
            <>
              <TextInput
                id={id}
                type="text"
                autoComplete="off"
                required
                disabled={!isOwner}
                value={wsSlug}
                onChange={(e) => setWsSlug(e.target.value)}
                hasError={!!slugError}
                {...ariaProps}
              />
              {slugChanged && (
                <Banner kind="info" className="mt-2">
                  {t("settings.slugWarning")}
                </Banner>
              )}
            </>
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          disabled={!isOwner || update.isPending || !(name ?? "").trim() || !(wsSlug ?? "").trim()}
        >
          {update.isPending ? t("settings.saving") : t("settings.save")}
        </Button>
      </form>
    </Card>
  );
}

// ── Danger zone card (delete workspace) ──────────────────────────────────────

function DangerZoneCard({ slug }: { readonly slug: string }) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const navigate = useNavigate();
  const ws = useWorkspace(slug);
  const del = useDeleteWorkspace(slug);
  const [confirmSlug, setConfirmSlug] = useState("");

  function handleDelete(e: FormEvent) {
    e.preventDefault();
    del.mutate(undefined, {
      onSuccess: () => navigate("/"),
    });
  }

  // Personal workspaces cannot be deleted
  if (ws.data?.isPersonal) {
    return (
      <Card title={t("settings.dangerZone")} danger>
        <p className="text-sm text-slate-400">
          {t("settings.personalCannotDelete")}
        </p>
      </Card>
    );
  }

  // Only owners may delete
  if (ws.data && ws.data.role !== "owner") {
    return null;
  }

  return (
    <Card title={t("settings.dangerZone")} danger>
      {del.error ? (
        <Banner kind="error" className="mb-4">{apiErrorMessage(del.error, tErr)}</Banner>
      ) : null}

      <p className="mb-4 text-sm text-slate-300">
        {t("settings.deleteDescription")}
      </p>

      <form onSubmit={handleDelete} className="space-y-4" noValidate>
        <Field label={t("settings.confirmLabel", { slug })}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="text"
              autoComplete="off"
              placeholder={slug}
              value={confirmSlug}
              onChange={(e) => setConfirmSlug(e.target.value)}
              {...ariaProps}
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="danger"
          disabled={del.isPending || confirmSlug !== slug}
        >
          {del.isPending ? t("settings.deleting") : t("settings.deleteWorkspace")}
        </Button>
      </form>
    </Card>
  );
}

// ── General section (rendered as the index settings tab) ─────────────────────

export function GeneralSettings() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return null;
  return (
    <div className="max-w-xl space-y-6">
      <GeneralCard slug={slug} />
      <DangerZoneCard slug={slug} />
    </div>
  );
}

// ── Settings shell (grouped sidebar nav + active section) ────────────────────

interface SettingsTab {
  readonly labelKey: string;
  readonly to: string;
  /** Match the index route when true (so /settings highlights "General"). */
  readonly end?: boolean;
}

function settingsTabs(slug: string): SettingsTab[] {
  return [
    { labelKey: "settings.tabGeneral", to: wsSettings(slug), end: true },
    { labelKey: "settings.tabTokens", to: wsTokens(slug) },
    { labelKey: "settings.tabTrash", to: wsTrash(slug) },
  ];
}

function tabClass({ isActive }: { isActive: boolean }): string {
  return isActive
    ? "block rounded-lg px-3 py-2 text-sm font-medium bg-slate-800/70 text-slate-100"
    : "block rounded-lg px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800/60 hover:text-slate-100";
}

export function WorkspaceSettingsPage() {
  const { t } = useTranslation("workspace");
  const { slug } = useParams<{ slug: string }>();

  if (!slug) return null;

  const tabs = settingsTabs(slug);

  return (
    <div className="mx-auto max-w-5xl p-8">
      <h1 className="mb-6 text-2xl font-semibold text-slate-100">{t("settings.title")}</h1>
      <div className="flex gap-8">
        <nav className="w-44 shrink-0 space-y-1">
          {tabs.map((tab) => (
            <NavLink key={tab.to} to={tab.to} end={tab.end} className={tabClass}>
              {t(tab.labelKey)}
            </NavLink>
          ))}
        </nav>
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
